import React, { useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { Icon } from 'leaflet';
import { useAnimatronics } from '../hooks/useAnimatronics';
import Hero from '../components/ui/Hero';
import AnimatronicFilters from '../components/filters/AnimatronicFilters';
import ScrollingBanner from '../components/sponsors/ScrollingBanner/Banner';
import 'leaflet/dist/leaflet.css';

const defaultIcon = new Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const MapPage = () => {
  const heroImage = "https://nifmkzddenyukgjcdetc.supabase.co/storage/v1/object/public/animatronics_data/logo_light.webp";
  const { animatronics, isLoading, error, uniqueCountries, uniqueParks, uniqueAttractions, uniqueTypes, uniqueStatuses, uniqueBrands } = useAnimatronics();
  
  const [filters, setFilters] = useState({
    search: '',
    country: '',
    park: '',
    attraction: '',
    type: '',
    status: '',
    brand: ''
  });

  const validAnimatronics = useMemo(() => {
    if (!animatronics) return [];
    
    return animatronics.filter(animatronic => {
      const matchesSearch = !filters.search || 
        animatronic.name.toLowerCase().includes(filters.search.toLowerCase());
      const matchesCountry = !filters.country || animatronic.country === filters.country;
      const matchesPark = !filters.park || animatronic.park === filters.park;
      const matchesAttraction = !filters.attraction || animatronic.attraction === filters.attraction;
      const matchesType = !filters.type || animatronic.type === filters.type;
      const matchesStatus = !filters.status || animatronic.status === filters.status;
      const matchesBrand = !filters.brand || animatronic.brand === filters.brand;

      const coords = animatronic.gps_coordinates?.split(',').map(Number);
      const hasValidCoords = coords?.length === 2 && 
        !isNaN(coords[0]) && !isNaN(coords[1]) &&
        coords[0] >= -90 && coords[0] <= 90 &&
        coords[1] >= -180 && coords[1] <= 180;

      return matchesSearch && matchesCountry && matchesPark && matchesAttraction && 
             matchesType && matchesStatus && matchesBrand && hasValidCoords;
    }).map(animatronic => ({
      ...animatronic,
      coordinates: animatronic.gps_coordinates.split(',').map(Number)
    }));
  }, [animatronics, filters]);

  if (error) {
    return (
      <div className="text-center py-8 text-red-400">
        Failed to load animatronics. Please try again later.
      </div>
    );
  }

  return (
    <div>
      <Hero
        title="Animatronics Map"
        description="Explore animatronic locations worldwide"
        backgroundImage={heroImage}
        compact
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatronicFilters
          filters={filters}
          onSearchChange={(value) => setFilters(prev => ({ ...prev, search: value }))}
          onCountryChange={(value) => setFilters(prev => ({ ...prev, country: value }))}
          onParkChange={(value) => setFilters(prev => ({ ...prev, park: value }))}
          onAttractionChange={(value) => setFilters(prev => ({ ...prev, attraction: value }))}
          onTypeChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
          onStatusChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
          onBrandChange={(value) => setFilters(prev => ({ ...prev, brand: value }))}
          countries={uniqueCountries || []}
          parks={uniqueParks || []}
          attractions={uniqueAttractions || []}
          types={uniqueTypes || []}
          statuses={uniqueStatuses || []}
          brands={uniqueBrands || []}
        />

        <div className="card overflow-hidden mb-8">
          <div className="h-[600px] rounded-lg overflow-hidden">
            <MapContainer
              center={[28.3852, -81.5639]}
              zoom={3}
              className="h-full w-full"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {!isLoading && validAnimatronics.map((animatronic) => (
                <Marker
                  key={animatronic.id}
                  position={[animatronic.coordinates[0], animatronic.coordinates[1]]}
                  icon={defaultIcon}
                >
                  <Popup>
                    <div className="text-center">
                      <h3 className="font-semibold mb-1">{animatronic.name}</h3>
                      <p className="text-sm text-gray-600 mb-1">{animatronic.park}</p>
                      <p className="text-sm text-gray-600 mb-2">{animatronic.attraction}</p>
                      <Link 
                        to={`/database/${animatronic.id}`}
                        className="text-sm text-brand hover:text-brand-light"
                      >
                        View Details →
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          </div>

       {/* <ScrollingBanner />*/}
      </div>
    </div>
  );
};

export default MapPage;